/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ContainerUserPaymentForm from 'components/UserPaymentForm';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { styles } from 'routes/renewMember/styles/renewMemberNewCard.styles';
import { useRenewSubscriptionWithNewCardMutation } from 'store/api/external-api.slice';
import { showMessage } from 'store/slices/message.slice';
import { extractTextFromHTML } from 'utils';

const RenewMemberNewCard = ({ setPaymentStatus, renewInfo }) => {
	const dispatch = useDispatch();

	const [loading, setLoading] = useState(true);

	const [renewSubscriptionWithNewCard] = useRenewSubscriptionWithNewCardMutation();

	const initialValues = useMemo(() => {
		if (renewInfo.member?.vid) {
			return {
				plan_type: '',
				first_name: renewInfo.member.first_name,
				last_name: renewInfo.member.last_name,
				email: renewInfo.member.email,
				save_card_information: true
			};
		}

		return {
			plan_type: '',
			first_name: '',
			last_name: '',
			email: '',
			save_card_information: true
		};
	}, [renewInfo.member]);

	const memberTypes = useMemo(() => {
		if (renewInfo.user?.member_types) {
			return renewInfo.user.member_types.map(type => ({ id: type.id, name: type.member_type }));
		}
		return [];
	}, [renewInfo.user]);

	const dispatchMessage = ({ message, variant = 'success' }) =>
		dispatch(
			showMessage({
				message,
				variant
			})
		);

	const processPayment = async data => {
		try {
			const renewData = {
				...data,
				sourceId: data.nonce,
				mc_form_token: renewInfo.form_token,
				mc_uid: renewInfo.user.id,
				mc_redirect: false
			};

			const formData = new FormData();

			Object.keys(renewData).forEach(key => {
				formData.append(key, renewData[key]);
			});

			const result = await renewSubscriptionWithNewCard(formData);

			if (result.data) {
				const message = extractTextFromHTML(result.data);

				dispatchMessage({
					message
				});
				setPaymentStatus('SUCCESS');
			} else {
				dispatchMessage({
					message: 'Payment Failed! Please, try again later',
					variant: 'error'
				});
				setPaymentStatus('ERROR');
			}
		} catch (e) {
			const message = e?.detail.includes('INVALID_CARD_DATA')
				? 'Payment Failed! Something wrong with your card data. Please, try again'
				: 'Payment Failed! Please, try again later';
			dispatchMessage({
				message,
				variant: 'error'
			});
			setPaymentStatus('ERROR');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Paper elevation={2} sx={styles.card}>
			{loading && (
				<Box sx={styles.centered}>
					<CircularProgress size={56} />
				</Box>
			)}
			<CardHeader
				disableTypography
				title={
					<Typography component={'span'} variant="h6" color="primary">
						Renew Member with New Card
					</Typography>
				}
			/>

			<CardContent>
				<ContainerUserPaymentForm
					isLoading={loading}
					onSubmitHandler={processPayment}
					isRenewForm
					memberTypes={memberTypes}
					setLoading={setLoading}
					initialValues={initialValues}
				/>
			</CardContent>
		</Paper>
	);
};

export default RenewMemberNewCard;
